import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { toast } from "sonner";
import { ChevronLeft, History as HistoryIcon, Trash2 } from "lucide-react";
import { getPracticeConfigs, PILLARS } from "./practiceConfigs";

const PILLAR_COLOR = { realize: "#5E7A73", designate: "#7A6A5E", release: "#8A6B7A" };

export default function PracticeHistory() {
  const t = useT();
  usePageMeta(t("practices_history_title"));
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const configs = useMemo(() => getPracticeConfigs(t), [t]);
  const iconFor = useMemo(() => {
    const m = {};
    PILLARS.forEach((p) => p.practices.forEach((pr) => { m[pr.key] = pr.icon; }));
    return m;
  }, []);

  const load = () => api.get("/practices/entries?limit=100").then(({ data }) => setEntries(Array.isArray(data) ? data : [])).catch(() => setEntries([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("practices_delete_confirm"))) return;
    try {
      await api.delete(`/practices/entries/${id}`);
      setEntries((prev) => prev.filter((x) => x.id !== id));
      toast.success(t("dw.deleted"));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="practice-history-screen">
      <button onClick={() => navigate("/app/practices")} data-testid="ph-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("practices_hub_title")}
      </button>
      <PageHeader eyebrow={t("dw.title")} title={t("practices_history_title")} subtitle={t("practices_history_sub")} />

      {entries === null ? (
        <Spinner label={t("common.loading")} />
      ) : entries.length === 0 ? (
        <EmptyState icon={HistoryIcon} title={t("practices_history_empty_title")} description={t("practices_history_empty_sub")} />
      ) : (
        <div className="space-y-3" data-testid="ph-list">
          {entries.map((e) => {
            const Icon = iconFor[e.practice_type] || HistoryIcon;
            const color = PILLAR_COLOR[e.pillar] || "#5E7A73";
            const title = configs[e.practice_type]?.title || e.practice_type;
            return (
              <div key={e.id} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`ph-entry-${e.id}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: color + "22" }}>
                  <Icon className="h-5 w-5" style={{ color }} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">{title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{shortDate(e.created_at)}</span>
                    <span className="rounded-full px-2 py-0.5" style={{ backgroundColor: color + "18", color }}>{t(`pillar_${e.pillar}`)}</span>
                    {typeof e.mood_before === "number" && typeof e.mood_after === "number" ? (
                      <span className="rounded-full bg-secondary/60 px-2 py-0.5">{e.mood_before} → {e.mood_after}</span>
                    ) : null}
                  </div>
                  {e.note ? <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{e.note}</p> : null}
                </div>
                <button onClick={() => remove(e.id)} data-testid={`ph-delete-${e.id}`} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
